import React, { useEffect, useState } from 'react';
import { useAppDispatch, useQueryString } from '@/utils/hooks';
import { useLocation } from 'react-router';
import { approveExternalLogin } from '@/services/users';
import { useReturnUrl } from '@/utils/returnUrl';
import { HttpStatusCode } from '@/HttpStatusCodes';
import SplashPage from '../SplashPage';
import { Trans } from '@lingui/react/macro';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { loadUser } from '@/state/global/creators';
import { BygrModule } from '@ygtechnologies/bygr-frontend-component-library';

interface ExternalUserValidateProps {}

interface QueryStringParams {
    token?: string;
    returnUrl?: string;
}

type Result = 'success' | 'token-error';

const ExternalUserValidate = ({}: ExternalUserValidateProps) => {
    const location = useLocation();
    const { token } = useQueryString<QueryStringParams>(location);
    const currentUser = useSelector((state: IState) => state.global.user);
    const navigateToReturnUrl = useReturnUrl(BygrModule.ChangeManagement);
    const [result, setResult] = useState<Result | null>(null);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!token) {
            return;
        }

        async function approve() {
            try {
                await approveExternalLogin(token!);
                await loadUser(dispatch);
                setResult('success');
                navigateToReturnUrl();
            } catch (e: any) {
                if (
                    e.status === HttpStatusCode.BadRequest ||
                    e.status === HttpStatusCode.NotFound ||
                    e.status === HttpStatusCode.Unauthorized
                ) {
                    if (currentUser?.isLoggedIn) {
                        navigateToReturnUrl();
                    } else {
                        setResult('token-error');
                    }
                } else {
                    throw e;
                }
            }
        }

        approve();
    }, [token]);

    function renderContent() {
        if (!result) {
            return <Trans>Please wait...</Trans>;
        }

        if (result === 'token-error') {
            return <Trans>Error</Trans>;
        }

        if (result === 'success') {
            return <Trans>Success, redirecting...</Trans>;
        }
    }

    return (
        <SplashPage>
            <h3>
                <Trans>Link user</Trans>
            </h3>
            {renderContent()}
        </SplashPage>
    );
};

export default ExternalUserValidate;
